import { ROOT_NETS } from '@somnhold/content';
import { hexDist, hexLen, hexToWorld, worldToHex, type Hex } from '../hex.ts';
import { idiv, imax } from '../math/fixed.ts';
import { deriveSeed, rngInt, seedRng } from '../math/rng.ts';
import { netNodes, rootNet, type RootNet } from './druid-layout.ts';
import { sapFlow, segD2, type SapNode } from './druid-winter-layout.ts';

/**
 * Inside the Grandsire (docs/sendas/druida.md, chapter 5): the grounds, the same from the seed in the sim and the
 * drawing, integers only. Up is −y (the top of the screen).
 *
 * - `mouthsLayout` (act 2, the buds below): a gallery hollowed in the living wood, the sap coming down from its top
 *   (the foot of the flow) to a fork, and from it to two mouths stopped with rot and to side galleries that drink it
 *   (drains: a fork with nothing after it); a knot on most branches (the crown's rule, `sapFlow`). Under each mouth, its
 *   root net (one of content ROOT_NETS or its mirror, the islet on the mouth): its two sound roots at the bottom, its
 *   stones between. The floor is the gallery and the nets; the rest is wood.
 * - `chamberLayout` (act 4, the Carcoma): the round chamber where the wood touches the titan's flesh, the Carcoma in
 *   the middle, its four roots to the walls (mud, resin, hidden, frost), each with its lesson's pieces; the door at the
 *   foot with the colliers; Elvira's three knots of sound root along the way from the middle to the door.
 */

export const MOUTHS_RADIUS = 11;
export const CHAMBER_RADIUS = 10;

type Pt = { x: number; y: number };

const allCells = (R: number): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};
const sortHex = (a: Hex, b: Hex): number => a.r - b.r || a.q - b.q;
const key = (h: Hex): string => `${h.q},${h.r}`;
const W = (h: Hex): Pt => hexToWorld(h.q, h.r);

/** The cells nobody walks: everything in the board but what lies within `w` of a segment or `r` of a pad. */
const carve = (R: number, segs: { a: Pt; b: Pt; w: number }[], pads: { p: Pt; r: number }[]): { rocks: Hex[]; floor: Hex[] } => {
  const rocks: Hex[] = [];
  const floor: Hex[] = [];
  for (const h of allCells(R)) {
    const p = W(h);
    let open = false;
    for (const s of segs) if (segD2(p.x, p.y, s.a.x, s.a.y, s.b.x, s.b.y) <= s.w * s.w) open = true;
    if (!open) for (const d of pads) if ((p.x - d.p.x) * (p.x - d.p.x) + (p.y - d.p.y) * (p.y - d.p.y) <= d.r * d.r) open = true;
    (open && hexLen(h.q, h.r) <= R - 1 ? floor : rocks).push(h);
  }
  rocks.sort(sortHex);
  floor.sort(sortHex);
  return { rocks, floor };
};

/* ---------- act 2: the buds below ---------- */

export interface MouthNet {
  /** The root net (node 0 the left sound root, 1 the right one, 2 the mouth, 3… the stones). */
  net: RootNet;
  /** Which of ROOT_NETS and mirrored. */
  pick: number;
  mirror: boolean;
}

export interface MouthsLayout {
  radius: number;
  rocks: Hex[];
  floor: Hex[];
  /** The gallery's flow: node 0 the fork the foot feeds; mouths and drains its leaves (a drain: a fork with no kids). */
  nodes: SapNode[];
  knots: { node: number; x: number; y: number }[];
  /** The mouths' nodes (in `nodes`), their nets (in the same order). */
  mouths: number[];
  nets: MouthNet[];
  foot: Pt;
  ceda: Pt;
  start: Pt;
  flip: boolean;
}

/** What a mouth asks (by its node) and how the knots must sit to give it (every set of closed knots that does). */
export const mouthWays = (nodes: readonly SapNode[], knots: readonly { node: number }[], mouth: number): number[][] => {
  const out: number[][] = [];
  for (let m = 0; m < 1 << knots.length; m++) {
    const closed = knots.map((_, i) => !!(m & (1 << i)));
    if (sapFlow(nodes, knots, 0, closed)[mouth] === 0) out.push(knots.map((_, i) => i).filter((i) => closed[i]));
  }
  return out;
};

/** A net of content ROOT_NETS (or its mirror) moved so its islet lies on `at`. */
const netAt = (pick: number, mirror: boolean, at: Hex, see: number): RootNet => {
  const raw = netNodes(ROOT_NETS[pick] as readonly number[], mirror);
  const isl = raw[2] as Hex;
  const nodes = raw.map((h) => ({ q: h.q - isl.q + at.q, r: h.r - isl.r + at.r }));
  return rootNet(nodes, see);
};

export const mouthsLayout = (seed: number, see = 3): MouthsLayout => {
  const R = MOUTHS_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0xb0d5));
  const flip = rngInt(rng, 2) === 1;
  const fq = (h: Hex): Hex => (flip ? { q: -h.q - h.r, r: h.r } : h);
  // The two mouths, a little off level, and their nets (each tried by the seed among the ones that fit beside the other).
  const mh = [fq({ q: -4, r: -1 }), fq({ q: 6, r: -2 })];
  const nets: MouthNet[] = [];
  const used = new Set<string>();
  for (let k = 0; k < 2; k++) {
    const opts: MouthNet[] = [];
    for (let pick = 0; pick < ROOT_NETS.length; pick++)
      for (const mirror of [false, true]) {
        const net = netAt(pick, mirror, mh[k] as Hex, see);
        if (net.nodes.some((h) => hexLen(h.q, h.r) > R - 2 || used.has(key(h)))) continue;
        // (Each net on its own side of the gallery: its roots never under the other mouth.)
        if (net.nodes.some((h) => hexDist(h, mh[1 - k] as Hex) < 3)) continue;
        opts.push({ net, pick, mirror });
      }
    if (!opts.length) throw new Error('mouthsLayout: no net fits');
    const o = opts[rngInt(rng, opts.length)] as MouthNet;
    for (const h of o.net.nodes) used.add(key(h));
    nets.push(o);
  }
  const m1 = W(mh[0] as Hex);
  const m2 = W(mh[1] as Hex);
  const fx = (p: Pt): Pt => (flip ? { x: -p.x, y: p.y } : p);
  const foot = fx({ x: 0, y: -8660 });
  const A = fx({ x: 0, y: -6062 });
  const B = fx({ x: 2500, y: -4330 });
  const d1 = fx({ x: -4000, y: -6928 });
  const d2 = fx({ x: 7000, y: -5196 });
  const d3 = fx({ x: 1500, y: -2598 });
  // What the mouths ask, by the seed: the left one 6 or 12 (one branch shut at the fork, or both), the right one 2 to
  // 6; never opened by the gallery as it comes (every knot open).
  const n1 = rngInt(rng, 2) === 0 ? 6 : 12;
  const n2 = [2, 3, 4, 6][rngInt(rng, 4)] as number;
  const nodes: SapNode[] = [
    { ...A, parent: -1, kids: [1, 2, 3], need: 0, knot: false, stage: 0 },
    { ...m1, parent: 0, kids: [], need: n1, knot: true, stage: 0 },
    { ...B, parent: 0, kids: [4, 5, 6], need: 0, knot: true, stage: 0 },
    { ...d1, parent: 0, kids: [], need: 0, knot: true, stage: 0 },
    { ...m2, parent: 2, kids: [], need: n2, knot: true, stage: 0 },
    { ...d2, parent: 2, kids: [], need: 0, knot: true, stage: 0 },
    { ...d3, parent: 2, kids: [], need: 0, knot: false, stage: 0 },
  ];
  const knots = nodes
    .map((n, i) => ({ n, i }))
    .filter((x) => x.n.knot)
    .map((x) => {
      const p = nodes[x.n.parent] as SapNode;
      return { node: x.i, x: idiv(p.x + x.n.x, 2), y: idiv(p.y + x.n.y, 2) };
    });
  const segs: { a: Pt; b: Pt; w: number }[] = [{ a: foot, b: A, w: 1150 }];
  const pads: { p: Pt; r: number }[] = [{ p: foot, r: 1700 }];
  nodes.forEach((n) => {
    if (n.parent >= 0) segs.push({ a: nodes[n.parent] as Pt, b: n, w: 900 });
    pads.push({ p: n, r: n.need ? 1300 : 1150 });
  });
  for (const m of nets) {
    const N = m.net;
    N.nodes.forEach((h, i) => {
      pads.push({ p: W(h), r: 1000 });
      N.edge[i]?.forEach((len, j) => len && segs.push({ a: W(h), b: W(N.nodes[j] as Hex), w: 700 }));
    });
  }
  const { rocks, floor } = carve(R, segs, pads);
  return { radius: R, rocks, floor, nodes, knots, mouths: [1, 4], nets, foot, ceda: { x: foot.x + (flip ? 1300 : -1300), y: foot.y + 300 }, start: { x: foot.x + (flip ? -900 : 900), y: foot.y + 900 }, flip };
};

/* ---------- act 4: the Carcoma's chamber ---------- */

/** One of the four roots: from the Carcoma to the wall. */
export interface ChamberRoot {
  kind: 'mud' | 'resin' | 'hidden' | 'frost';
  wall: Pt;
}

export interface ChamberLayout {
  radius: number;
  rocks: Hex[];
  mid: Pt;
  door: Pt;
  roots: ChamberRoot[];
  /** The mud root: its net (the islet on the root). */
  mud: RootNet;
  /** The resin root: its patches across the root (the root's at `rootAt`), and which way the air runs along them as it starts (+1: to the higher index). */
  patches: Pt[];
  rootAt: number;
  wind: number;
  /**
   * The hidden root: the cells it runs through (in sight its first and last `seen`, under the wood between), the sound
   * root under the floor crossing it; for each cell of either, its mushrooms (black: over the rot; white: over sound).
   */
  hidden: { cells: Hex[]; rot: boolean[]; black: number[]; white: number[]; vis: number[] };
  /** The frost root: its crown (node 0 the fork the spring feeds; three buds and drains), its knots, the spring. */
  frost: { nodes: SapNode[]; knots: { node: number; x: number; y: number }[]; spring: Pt; buds: number[] };
  /** Elvira's knots of sound root along the way to the door: where the hero touches one, where its gate shuts the way. */
  gates: { at: Pt; bar: Pt }[];
  colliers: Pt[];
  elvira: Pt;
  ceda: Pt;
  espino: Pt;
  /** Where the slimes come out of the walls. */
  holes: Pt[];
  start: Pt;
}

/** The frost root's crown: every set of its knots closed that opens its three buds on one pulse. */
export const frostWays = (nodes: readonly SapNode[], knots: readonly { node: number }[], buds: readonly number[]): number[][] => {
  const out: number[][] = [];
  for (let m = 0; m < 1 << knots.length; m++) {
    const closed = knots.map((_, i) => !!(m & (1 << i)));
    const f = sapFlow(nodes, knots, 0, closed);
    if (buds.every((b) => f[b] === 0)) out.push(knots.map((_, i) => i).filter((i) => closed[i]));
  }
  return out;
};

export const chamberLayout = (seed: number, see = 3): ChamberLayout => {
  const R = CHAMBER_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0xca2c));
  const mid = { x: 0, y: -866 };
  const door = { x: 0, y: 6928 };
  const roots: ChamberRoot[] = [
    { kind: 'mud', wall: { x: -7000, y: -5196 } },
    { kind: 'resin', wall: { x: 7000, y: -5196 } },
    { kind: 'hidden', wall: { x: 7000, y: 4330 } },
    { kind: 'frost', wall: { x: -6500, y: 4330 } },
  ];
  // Mud: the document's net, its islet on the root near the wall.
  const mud = netAt(0, false, worldToHex(-5000, -3464), see);
  // Resin: seven patches across the root, the root's the middle one; the air's way by the seed.
  const patches: Pt[] = [];
  for (let k = -3; k <= 3; k++) patches.push({ x: 4200 + k * 650, y: -3118 + k * 880 });
  const wind = rngInt(rng, 2) === 0 ? 1 : -1;
  // Hidden: from the Carcoma's side down to the right wall, a hex at a time; two cells in sight each end.
  const hc: Hex[] = [];
  const h0 = worldToHex(1500, 0);
  const steps = [
    { q: 1, r: 0 },
    { q: 0, r: 1 },
    { q: 1, r: 0 },
    { q: 0, r: 1 },
    { q: 1, r: 0 },
    { q: 0, r: 1 },
  ];
  hc.push(h0);
  for (const d of steps) hc.push({ q: (hc[hc.length - 1] as Hex).q + d.q, r: (hc[hc.length - 1] as Hex).r + d.r });
  // The sound root under the floor crosses it at the first cell under the wood (the cell that looks the nearest to cut).
  const cross = 2 + rngInt(rng, 2);
  const c = hc[cross] as Hex;
  const sound: Hex[] = [
    { q: c.q - 2, r: c.r + 1 },
    { q: c.q - 1, r: c.r + 1 },
    c,
    { q: c.q + 1, r: c.r - 1 },
    { q: c.q + 2, r: c.r - 2 },
  ];
  const cells: Hex[] = [];
  const rot: boolean[] = [];
  const black: number[] = [];
  const white: number[] = [];
  const vis: number[] = [];
  const at = (h: Hex): number => {
    const i = cells.findIndex((x) => x.q === h.q && x.r === h.r);
    if (i >= 0) return i;
    cells.push(h);
    rot.push(false);
    black.push(0);
    white.push(0);
    vis.push(0);
    return cells.length - 1;
  };
  hc.forEach((h, k) => {
    const i = at(h);
    rot[i] = true;
    if (k < 2 || k >= hc.length - 2) vis[i] = 1;
    else black[i] = (black[i] as number) + 1;
  });
  sound.forEach((h, k) => {
    const i = at(h);
    if (k === 0 || k === sound.length - 1) {
      if (!vis[i]) vis[i] = 2;
    } else white[i] = (white[i] as number) + 1;
  });
  // Frost: a crown off a spring in the lower left wall; what its buds ask by the seed (one way to open all three).
  const spring = { x: -7400, y: 5196 };
  const F = { x: -5600, y: 4330 };
  const G = { x: -3600, y: 3464 };
  const asks = [
    [6, 3],
    [6, 2],
    [4, 2],
  ][rngInt(rng, 3)] as number[];
  const nodes: SapNode[] = [
    { ...F, parent: -1, kids: [1, 2, 3], need: 0, knot: false, stage: 0 },
    { x: -6800, y: 2598, parent: 0, kids: [], need: asks[0] as number, knot: true, stage: 0 },
    { ...G, parent: 0, kids: [4, 5, 6], need: 0, knot: true, stage: 0 },
    { x: -5200, y: 6062, parent: 0, kids: [], need: 0, knot: true, stage: 0 },
    { x: -2600, y: 2000, parent: 2, kids: [], need: asks[1] as number, knot: true, stage: 0 },
    { x: -1900, y: 4600, parent: 2, kids: [], need: asks[1] as number, knot: true, stage: 0 },
    { x: -3200, y: 5600, parent: 2, kids: [], need: 0, knot: true, stage: 0 },
  ];
  const knots = nodes
    .map((n, i) => ({ n, i }))
    .filter((x) => x.n.knot)
    .map((x) => {
      const p = nodes[x.n.parent] as SapNode;
      return { node: x.i, x: idiv(p.x + x.n.x, 2), y: idiv(p.y + x.n.y, 2) };
    });
  const gates = [1732, 3464, 5196].map((y) => ({ at: { x: -1100, y }, bar: { x: 0, y } }));
  // The wall: the chamber's rim, and the titan's flesh at its back (the top), a little lower there.
  const rocks: Hex[] = [];
  for (const h of allCells(R)) {
    const p = W(h);
    if (hexLen(h.q, h.r) >= R) rocks.push(h);
    else if (p.y < -7200 && hexLen(h.q, h.r) >= R - 1) rocks.push(h);
  }
  rocks.sort(sortHex);
  void imax;
  return {
    radius: R,
    rocks,
    mid,
    door,
    roots,
    mud,
    patches,
    rootAt: 3,
    wind,
    hidden: { cells, rot, black, white, vis },
    frost: { nodes, knots, spring, buds: [1, 4, 5] },
    gates,
    colliers: [
      { x: -1300, y: 6700 },
      { x: 0, y: 7400 },
      { x: 1300, y: 6700 },
    ],
    elvira: { x: -2000, y: 3600 },
    ceda: { x: 2200, y: 6400 },
    espino: { x: -2600, y: 7000 },
    holes: [
      { x: -8200, y: -1732 },
      { x: 8200, y: -1732 },
      { x: -7600, y: 2600 },
      { x: 7800, y: 1732 },
    ],
    start: { x: 0, y: 4400 },
  };
};
